<template>
  <div>
    <ProfessionTaskCityPage v-if="taskCityPage" v-bind="taskCityPage" />
    <!-- Profession-City Page -->
    <template v-else-if="profession && city">
      <JoszakiPage inner-class="flex flex-col gap-4">
        <BreadCrumbs :links="getBreadCrumbs" />
        <!-- Header -->
        <div ref="header">
          <JoszakiHeading class="text-center mb-4">
            {{
              $t("personList.lookingFor", {
                city: city.name,
                profession: $helpers.capitalize(profession.theperson),
              })
            }}
          </JoszakiHeading>

          <div class="flex flex-col md:flex-row gap-3 w-full items-stretch">
            <div class="w-full grid gap-4">
              <QuickOffer
                class="flex-shrink flex-grow-[2]"
                :placeholder="quickOfferPlaceholder"
                @requestTender="
                  onRequestTender({ source: 'quickOffer', text: $event })
                "
              />
            </div>
          </div>
        </div>

        <div class="listpage-info">
          <div class="font-700 text-primary text-2xl text-center">
            {{
              $t("personList.findPerson", {
                professionObjective: profession.objective,
              })
            }}
          </div>
        </div>

        <NotFoundPersonList
          v-if="!found"
          class="w-full p-3"
          :profession-name="professionName.toLowerCase()"
        />
        <JoszakiLoading
          :active="loading && personList.length < 1"
          :is-full-page="false"
        />
        <OnlineCoverageBanner
          v-if="isOnlineProfession && found"
          class="sticky top-20 z-30"
        />
        <!-- A/B test (profession-city-page-vertical-card-mobile): vertical
             portrait cards in a two-column layout on mobile -->
        <div v-if="useVerticalCardMobile" class="grid grid-cols-2 gap-3">
          <template v-if="loading">
            <CardPersonPlaceholder v-for="i in limit" :key="i" />
          </template>
          <template v-else>
            <ProfessionalPortraitCard
              v-for="person in personList"
              :key="person.id"
              class="h-full"
              :person="person"
              :profession-name="profession.name"
              :highlighted-profession="profession"
              :highlighted-city="city"
              :show-city="false"
              @select="
                onClickPersonCard(person, personList.indexOf(person) + 1)
              "
              @callProfessional="onCallProfessional(person)"
              @requestTender="onRequestTender({ person, source: 'personCard' })"
            />
          </template>
        </div>
        <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <template v-if="loading">
            <CardPersonPlaceholder v-for="i in limit" :key="i" />
          </template>
          <template v-else>
            <CardPerson
              v-for="person in personList"
              :key="person.id"
              class="h-full"
              :highlighted-city="city"
              :highlighted-profession="profession"
              :person="person"
              show-active-indicator
              @select="
                onClickPersonCard(person, personList.indexOf(person) + 1)
              "
              @callProfessional="onCallProfessional(person)"
              @requestTender="onRequestTender({ person, source: 'personCard' })"
            />
          </template>
        </div>

        <!-- Load more -->
        <PaginationComponent
          v-if="paginationInfo && paginationInfo.last !== 1"
          :pagination-info="paginationInfo"
        />

        <OfferBox
          class="max-lg:hidden"
          :placeholder="$t('quickOffer.placeholder')"
          @requestTender="onRequestTender({ source: 'offerBox', text: $event })"
        />
        <BlockTender
          class="lg:hidden"
          text-input
          @requestTender="
            onRequestTender({ source: 'banner-block', text: $event })
          "
        />

        <div
          v-if="content"
          class="flex flex-col-reverse lg:grid lg:grid-cols-[3fr_1fr] gap-3"
        >
          <JoszakiHtmlTextBlock :text="content" />
          <div>
            <TableOfContents class="lg:sticky lg:top-3" :content="content" />
          </div>
        </div>

        <!-- People rating summary -->
        <!--
        <div v-if="found" class="block text-center mb-4">
          <PeopleRating
            :city-name="city.name"
            :profession="profession"
            :total-score="totals.averageScore / 2"
            :total-score-count="totals.totalScoreCount"
          />
        </div>
        -->
      </JoszakiPage>
      <ProfessionTaskLinks
        :popular-profession-tasks="popularProfessionTasks"
        :profession="profession"
      />
    </template>

    <!-- Profession-County Page -->
    <template v-else-if="profession && county">
      <JoszakiPage>
        <JoszakiHeading>
          {{
            $t("listPage.county.fromCounty", {
              professionPlural: profession.plural,
              countyName: county.name,
            })
          }}
        </JoszakiHeading>
        <QuickOffer
          :placeholder="quickOfferPlaceholder"
          class="mb-8"
          @requestTender="
            onRequestTender({ source: 'quickOffer', text: $event })
          "
        />
        <CityCountLinks
          v-if="cityPersonCounts?.length"
          :cities="cityPersonCounts"
          :profession="profession"
        />
        <div v-else>
          {{ $t("listPage.county.noneFound") }}
        </div>
        <JoszakiButton
          type="info"
          :to="
            localePath({
              name: 'professionPage',
              params: { profession: profession.seoName },
            })
          "
        >
          {{
            $t("listPage.county.checkRest", {
              professionObjective: profession.objective,
            })
          }}
        </JoszakiButton>
      </JoszakiPage>
    </template>
    <NotFound v-else @requestTender="onRequestTender({ source: 'notFound' })" />
  </div>
</template>

<router>
{
name: 'professionals'
}
</router>

<script>
import PROFESSION_CITY_PAGE_SSR from "./professionCityPage.graphql";
import PROFESSION_CITY_POPULAR_TASKS from "./professionCityPopularTasks.graphql";
import { permanentRedirect, redirectHomeOnMissing } from "~/helpers/redirect";
import ProfessionTaskCityPage from "~/components/_refactored/professionTaskPage/ProfessionTaskCityPage.vue";
import {
  loadProfessionTaskCityPage,
  loadProfessionTaskCountyPage,
} from "~/components/_refactored/professionTaskPage/loadProfessionTaskPage";
import NunitoVariableLatin from "~/assets/fonts/nunito-variable-latin.woff2";
import NunitoVariableLatinExt from "~/assets/fonts/nunito-variable-latin-ext.woff2";

import BreadCrumbs from "~/components/_refactored/BreadCrumbs.vue";
import QuickOffer from "~/components/_refactored/app/boxes/QuickOffer.vue";
import PaginationComponent from "~/components/_refactored/common/PaginationComponent.vue";
import CityCountLinks from "~/components/mainpage/CityCountLinks.vue";
import NotFoundPersonList from "~/components/people/NotFoundPersonList.vue";
import OfferBox from "~/components/people/OfferBox.vue";

import TableOfContents from "~/components/_refactored/app/boxes/TableOfContents.vue";
import BlockTender from "~/components/_refactored/blocks/BlockTender.vue";
import OnlineCoverageBanner from "~/components/_refactored/blocks/OnlineCoverageBanner.vue";
import CardPersonPlaceholder from "~/components/_refactored/card/CardPersonPlaceholder.vue";
import JoszakiHtmlTextBlock from "~/components/_refactored/common/JoszakiHtmlTextBlock.vue";
import { useFetchProfessionals } from "~/components/_refactored/profession";
import CardPerson from "~/components/people/CardPerson.vue";
import ProfessionalPortraitCard from "~/components/_refactored/indexPage/front-page-modules/ProfessionalPortraitCard.vue";
import ProfessionTaskLinks from "~/components/people/ProfessionTaskLinks.vue";
import { useRequestTender } from "~/composables/requestTender";
import { useNewTender } from "~/composables/tender/useNewTender";
import {
  useTrackFrontendEvent,
  FRONTEND_EVENT_TYPE,
  FRONTEND_EVENT_PLACEMENT,
} from "~/composables/tracking/useTrackFrontendEvent";
import errorCodes from "~/helpers/errorCodes";
import { generatePreloadLinks } from "~/helpers/files";
import CITIES_LIST from "~/graphql/citiesList.graphql";
import { useUserStore } from "~/stores/user";
import { useProfessionNavLinksStore } from "~/stores/professionNavLinks";
import { hashParamsMixin } from "~/composables/useHashParams";
import GET_PROFESSION_CITIES from "~/components/_refactored/conductor/professions/getProfessionCities.graphql";
import { isOnlineProfessionEnabled } from "~/experiments/onlineProfession";
import { isProfessionCityPageEmpty } from "~/components/_refactored/professionPage/professionCityIndexing";
import { emptyPageRobotsMeta } from "~/components/_refactored/professionPage/emptyPageRobotsMeta";

export default {
  name: "ProfessionalsPage",
  components: {
    ProfessionTaskLinks,
    BlockTender,
    PaginationComponent,
    NotFoundPersonList,
    QuickOffer,
    OfferBox,
    CityCountLinks,
    BreadCrumbs,
    NotFound: () => import("~/components/listpage/NotFound.vue"),
    CardPerson,
    ProfessionalPortraitCard,
    OnlineCoverageBanner,
    TableOfContents,
    CardPersonPlaceholder,
    JoszakiHtmlTextBlock,
    ProfessionTaskCityPage,
  },
  mixins: [hashParamsMixin],
  layout: "DefaultLayout",
  setup() {
    const newTender = useNewTender();
    newTender.showFloater();

    const fetchProfessionals = useFetchProfessionals();
    const { trackFrontendEvent } = useTrackFrontendEvent();
    return {
      userStore: useUserStore(),
      ...useRequestTender(),
      fetchProfessionals,
      trackFrontendEvent,
    };
  },
  async asyncData(context) {
    // Note: Hash-based pagination - server always loads page 1
    // Client-side mounted() will load the correct page based on hash

    const fetchProfessionals = useFetchProfessionals(context);

    const professionSeoName = context.route.params.profession;
    const citySeoName = context.route.params.city;
    const parentCitySeoName = context.route.params.parentCity;
    const requestedPage = 1; // Always fetch page 1 on SSR (hash not visible to server)

    try {
      const {
        cityBySeoName: city,
        countyBySeoName: county,
        professionBySeoName: profession,
        professionCityByProfessionAndCitySeoName: professionCity,
      } = await context.$query(PROFESSION_CITY_PAGE_SSR, {
        professionSeoName,
        citySeoName,
      });

      const limit = context.$device.isMobile ? 20 : 50;

      // Redirect /szakemberek/:profession/amp/:city to /szakemberek/:profession/:city (permanent)
      if (parentCitySeoName === "amp") {
        const redirectPath = context.localePath({
          name: "professionals",
          params: {
            profession: professionSeoName,
            city: citySeoName,
          },
        });
        permanentRedirect({
          res: context.res,
          redirect: context.redirect,
          location: redirectPath,
        });
        return;
      }

      // Redirect /szakemberek/:profession/:parentCity/:city to /szakemberek/:profession/:city
      // if parentCity is a valid parent city or county for the city
      if (parentCitySeoName) {
        const actualParentCitySeoName = city?.parentCity?.seoName;
        const actualCountySeoName = city?.county?.seoName;
        const isValidParent =
          parentCitySeoName === actualParentCitySeoName ||
          parentCitySeoName === actualCountySeoName;

        if (!isValidParent) {
          redirectHomeOnMissing(context);
          return;
        }

        // Redirect to shorter URL without parentCity
        const redirectPath = context.localePath({
          name: "professionals",
          params: {
            profession: professionSeoName,
            city: citySeoName,
          },
        });
        permanentRedirect({
          res: context.res,
          redirect: context.redirect,
          location: redirectPath,
        });
        return;
      }

      if (!profession && (city || county)) {
        const taskPage = city
          ? await loadProfessionTaskCityPage(context, {
              slug: professionSeoName,
              citySeoName,
            })
          : await loadProfessionTaskCountyPage(context, professionSeoName);
        if (taskPage?.redirected) {
          return;
        }
        if (taskPage) {
          return taskPage.data;
        }
      }

      if ((!city && !county) || !profession) {
        redirectHomeOnMissing(context);
        return;
      }

      if (city) {
        // Check if this city is in the profession's allowed city list
        const professionCitiesResult = await context.$query(
          GET_PROFESSION_CITIES,
          { professionId: String(profession.id) }
        );
        const allowedCityIds = (
          professionCitiesResult.professionCityListByProfessionId || []
        ).map((item) => item.cityId);

        // If the profession has configured cities and this city is not allowed,
        // redirect to the county page
        if (allowedCityIds.length > 0 && !allowedCityIds.includes(city.id)) {
          const countySeoName = city.county?.seoName;
          if (countySeoName) {
            const redirectPath = context.localePath({
              name: "professionals",
              params: {
                profession: professionSeoName,
                city: countySeoName,
              },
            });
            permanentRedirect({
              res: context.res,
              redirect: context.redirect,
              location: redirectPath,
            });
            return;
          }
        }

        const [professionals, popularTasks] = await Promise.all([
          fetchProfessionals({
            page: requestedPage,
            pageSize: limit,
            professionSeoName,
            citySeoName,
          }),
          context.$query(PROFESSION_CITY_POPULAR_TASKS, {
            professionSeoName,
            citySeoName,
          }),
        ]);

        const popularProfessionTasks =
          popularTasks.popularProfessionTasksByProfession ?? [];

        await Promise.all([
          context.store.dispatch("listPage/setCity", city),
          context.store.dispatch("listPage/setProfession", profession),
          context.store.dispatch("listPage/setProfessionTask", null),
          context.store.dispatch("listPage/setCounty", null),
          useProfessionNavLinksStore(context.$pinia).load(context, {
            professionSeoName,
          }),
        ]);

        const content =
          professionCity?.contentHtml ??
          profession.cityPageContentHtml.replaceAll(
            "{cityName}",
            city?.name ?? ""
          );

        return {
          limit,
          city,
          county: null,
          profession,
          professionCity,
          totals: {
            averageScore: professionals?.peopleScores?.averageScore ?? 0,
            totalScoreCount: professionals?.peopleScores?.totalScoreCount ?? 0,
          },
          personList: professionals?.people ?? [],
          isEmptyCityPage: isProfessionCityPageEmpty(professionals),
          cityPersonCounts: [],
          paginationInfo: professionals.pagination,
          popularProfessionTasks,
          content,
        };
      } else if (county) {
        const [citiesResult, professionCitiesResult] = await Promise.all([
          context.$query(CITIES_LIST, {
            input: {
              countySeoName: citySeoName,
              order: "desc",
              orderBy: "population",
            },
          }),
          context.$query(GET_PROFESSION_CITIES, {
            professionId: String(profession.id),
          }),
        ]);

        const allCityPersonCounts = citiesResult.cities ?? [];

        const allowedCityIds = (
          professionCitiesResult.professionCityListByProfessionId || []
        ).map((item) => item.cityId);

        const cityPersonCounts =
          allowedCityIds.length > 0
            ? allCityPersonCounts.filter((city) =>
                allowedCityIds.includes(city.id)
              )
            : allCityPersonCounts;

        await Promise.all([
          context.store.dispatch("listPage/setCity", null),
          context.store.dispatch("listPage/setProfession", profession),
          context.store.dispatch("listPage/setProfessionTask", null),
          context.store.dispatch("listPage/setCounty", county),
          useProfessionNavLinksStore(context.$pinia).load(context, {
            professionSeoName,
          }),
        ]);

        return {
          city: null,
          county,
          profession,
          totals: {
            averageScore: 0.0,
            totalScoreCount: 0,
          },
          personList: [],
          cityPersonCounts,
          paginationInfo: null,
          popularProfessionTasks: [],
        };
      }
    } catch (error) {
      context.$logger.error(
        errorCodes.PEOPLE_BY_CITY_AND_PROFESSION_ASYNC_DATA_ERROR,
        error
      );
      context.error({
        statusCode: 500,
        message: context.i18n.t("error.codes.INTERNAL_SERVER_ERROR"),
      });
    }
    return {
      limit: 20,
      city: undefined,
      profession: undefined,
      totals: {
        averageScore: 0.0,
        totalScoreCount: 0,
      },
      personList: [],
      paginationInfo: null,
    };
  },
  data() {
    return {
      loading: false,
      hashParamsScrollRef: "header", // Used by hashParamsMixin for scroll-to behavior
      taskCityPage: null,
    };
  },
  head() {
    if (this.taskCityPage) {
      return {};
    }
    if (this.profession && this.city) {
      const nthPage =
        this.paginationInfo?.current > 1
          ? this.$t("seo.professionalsCityPage.pageNumber", {
              pageNumber: this.paginationInfo?.current,
            })
          : "";
      const title = this.$t("seo.professionalsCityPage.title", {
        profession: this.$helpers.capitalize(this.profession.theperson),
        professionPlural: this.profession.plural,
        cityName: this.city.name,
        pageNumber: nthPage,
      }).trim();
      const description =
        this.professionCity?.metaDescription ||
        this.$t("seo.professionalsCityPage.description", {
          personCount: this.paginationInfo?.total ?? 0,
          cityName: this.city.name.toLowerCase(),
          professionTheperson: this.profession.theperson,
        });

      return {
        title,
        meta: [
          {
            hid: "og:title",
            property: "og:title",
            content: title,
          },
          {
            hid: "og:url",
            property: "og:url",
            content: `${this.$helpers.topDomain}${this.$route.fullPath}`,
          },
          {
            hid: "og:image",
            property: "og:image",
            content: "/joszaki-head.png",
          },
          {
            hid: "description",
            name: "description",
            content: description,
          },
          {
            hid: "og:description",
            property: "og:description",
            content: description,
          },
          ...emptyPageRobotsMeta({ isEmpty: this.isEmptyCityPage }),
        ],
        link: [
          {
            rel: "canonical",
            // Hash-based pagination: canonical URL should not include page parameter
            // All paginated views are considered the same canonical page
            href: `${this.$helpers.topDomain}${this.$route.path}`,
          },
        ].concat(
          generatePreloadLinks([NunitoVariableLatin, NunitoVariableLatinExt])
        ),
      };
    }
    if (this.profession && this.county) {
      const title = this.$t("seo.professionalsCountyPage.title", {
        professionPlural: this.profession.plural,
        countyName: this.county.name,
      });
      const description =
        this.professionCity?.metaDescription ||
        this.$t("seo.professionalsCountyPage.description");
      return {
        title,
        meta: [
          {
            hid: "og:title",
            property: "og:title",
            content: title,
          },
          {
            hid: "description",
            name: "description",
            content: description,
          },
          {
            hid: "og:url",
            property: "og:url",
            content: `${this.$helpers.topDomain}${this.$route.fullPath}`,
          },
          {
            hid: "og:description",
            property: "og:description",
            content: description,
          },
          {
            hid: "og:image",
            property: "og:image",
            content: "/joszaki-head.png",
          },
        ],
        link: [
          {
            rel: "canonical",
            href: `${this.$helpers.topDomain}${this.$route.path}`,
          },
        ].concat(
          generatePreloadLinks([NunitoVariableLatin, NunitoVariableLatinExt])
        ),
      };
    }
    return {
      title: this.$t("seo.professionalsCityPage.notFoundTitle"),
      meta: [
        {
          name: "robots",
          content: "noindex",
        },
      ],
      link: generatePreloadLinks([NunitoVariableLatin, NunitoVariableLatinExt]),
    };
  },
  computed: {
    quickOfferPlaceholder() {
      if (!this.profession) {
        return null;
      }

      const professionsWithFixSpecificPlaceholder = [
        "acs",
        "badogos",
        "haztartasi-gep-szerviz",
      ];
      const shouldUseProfessionSpecificPlaceholder =
        professionsWithFixSpecificPlaceholder.includes(this.profession.seoName);
      if (shouldUseProfessionSpecificPlaceholder) {
        return this.$t(`quickOfferPlaceholder.${this.profession.seoName}`);
      }

      return null;
    },
    getBreadCrumbs() {
      const links = [];

      if (this.profession?.name) {
        if (this.profession.professionGroup) {
          const professionsPagePath = this.localePath({
            name: "professionsLandingPage",
            hash: `#${this.profession.professionGroup.seoName}`,
          });
          links.push({
            name: this.profession.professionGroup.name,
            page: professionsPagePath,
          });
        }
        links.push({
          name: this.profession.plural,
          page: this.localePath({
            name: "professionPage",
            params: { profession: this.profession.seoName },
          }),
        });
      }
      if (this.city?.name && this.profession?.name) {
        links.push({
          name: this.$t("listPage.breadCrumb.page", {
            city: this.city.name,
            professionPlural: this.profession.plural.toLowerCase(),
          }),
          link: "",
        });
      }

      return links;
    },
    isOnlineProfession() {
      return isOnlineProfessionEnabled(this.$ab) && !!this.profession?.online;
    },
    // A/B test: on mobile, show vertical ProfessionalPortraitCard in a
    // two-column layout instead of the single-column CardPerson list.
    // $device.isMobile is user-agent based so it stays consistent between SSR
    // and hydration, avoiding a layout flash.
    useVerticalCardMobile() {
      return (
        this.$device.isMobile &&
        this.$ab.isVariant("profession-city-page-vertical-card-mobile", "test")
      );
    },
    totalScore() {
      return this.totals.averageScore / 2;
    },
    found() {
      return this.profession && this.city && this.personList.length;
    },
    professionName() {
      if (!this.profession || !this.profession.theperson) {
        return "";
      }
      return this.profession.theperson;
    },
    peopleIds() {
      return this.personList.map((x) => {
        return x.id;
      });
    },
  },
  watch: {
    // Note: Hash-based pagination - no need to watch $route for page changes
    // Hash changes are handled by the hashchange event listener in mounted()
  },
  beforeMount() {
    if (this.taskCityPage) {
      return;
    }
    this.trackViewItemList();
  },
  mounted() {
    if (this.taskCityPage) {
      return;
    }
    this.trackPageView();
    this.seoTrackCurrentPage();
    // Note: Hash-based pagination is handled by hashParamsMixin via $route.hash watcher
  },
  beforeDestroy() {
    this.trackPageLeave();
  },
  methods: {
    /**
     * Called by hashParamsMixin when hash params change
     * @param {number} page - Page number from hash
     */
    loadPageData(page) {
      if (this.taskCityPage) {
        return;
      }
      this.loadProfessionalsPage(page);
    },

    trackPageView() {
      if (this.profession && this.city) {
        this.$trackers.pageview({
          pagename: this.$trackers.pageNames.PROFESSION_CITY_PAGE,
          pagenumber: this.paginationInfo?.current,
          professionSeoName: this.profession?.seoName,
          citySeoName: this.city?.seoName,
          professionGroupSeoName: this.profession?.professionGroup?.seoName,
          personIds: this.peopleIds,
        });
      } else if (this.profession && this.county) {
        this.$trackers.pageview({
          pagename: this.$trackers.pageNames.PROFESSION_COUNTY_PAGE,
          pagenumber: this.paginationInfo?.current,
          professionSeoName: this.profession?.seoName,
          countySeoName: this.county?.seoName,
          professionGroupSeoName: this.profession?.professionGroup?.seoName,
        });
      }
    },
    trackPageLeave() {
      if (this.profession && this.city) {
        this.$trackers.pageleave({
          pagename: this.$trackers.pageNames.PROFESSION_CITY_PAGE,
          pagenumber: this.paginationInfo?.current,
        });
      } else if (this.profession && this.county) {
        this.$trackers.pageleave({
          pagename: this.$trackers.pageNames.PROFESSION_COUNTY_PAGE,
          pagenumber: this.paginationInfo?.current,
        });
      }
    },
    async loadProfessionalsPage(requestedPage) {
      this.loading = true;
      try {
        // Track page leave when navigating between pages
        // hashPage is provided by hashParamsMixin
        if (requestedPage > 1 || this.hashPage > 1) {
          this.trackPageLeave();
        }

        const resp = await this.fetchProfessionals({
          professionSeoName: this.profession.seoName,
          citySeoName: this.city.seoName,
          pageSize: this.limit,
          page: requestedPage ? parseInt(requestedPage) : 1,
        });
        this.paginationInfo = resp.pagination;
        this.personList = resp.people;
        // Track views when page changes
        if (requestedPage) {
          this.trackViewItemList();
          this.seoTrackCurrentPage();
          this.trackPageView();
        }
      } catch (error) {
        console.error("Failed to load more people", error);
        this.$sentry.captureException(error);
        this.$sentry.captureException(JSON.stringify(error));
      }
      this.loading = false;
    },
    onCallProfessional(person) {
      this.trackFrontendEvent({
        eventType: FRONTEND_EVENT_TYPE.PHONE_CLICK,
        personId: person.id,
        placement: FRONTEND_EVENT_PLACEMENT.PERSON_CARD,
        metadata: {
          professionSeoName: this.profession?.seoName,
          citySeoName: this.city?.seoName,
        },
      });

      this.trackOpenPersonProfile(person);

      this.$trackers.trackEvent({
        title: "Clicked On Mate Phone Number",
        data: {
          personId: person.id,
          professionSeoName: this.profession.seoName,
          citySeoName: this.city.seoName,
          countySeoName: this.county?.seoName,
          professionGroupSeoName: this.profession?.professionGroup?.seoName,
        },
      });

      const basePath = this.localePath({
        name: "professionalPage",
        params: {
          professionalSeoName: person.seoName,
        },
      });
      const hashParams = [];
      if (this.city?.seoName) {
        hashParams.push(`city=${this.city.seoName}`);
      }
      if (this.profession?.seoName) {
        hashParams.push(`profession=${this.profession.seoName}`);
      }
      const path =
        hashParams.length > 0
          ? `${basePath}#${hashParams.join("&")}`
          : basePath;
      window.open(path);
    },
    onRequestTender({ person, source, text }) {
      this.requestTender({
        person,
        profession: this.profession,
        city: this.city,
        source,
        description: text,
      });
    },
    onClickPersonCard(person) {
      this.trackOpenPersonProfile(person);

      const basePath = this.localePath({
        name: "professionalPage",
        params: {
          professionalSeoName: person.seoName,
        },
      });
      const hashParams = [];
      if (this.city?.seoName) {
        hashParams.push(`city=${this.city.seoName}`);
      }
      if (this.profession?.seoName) {
        hashParams.push(`profession=${this.profession.seoName}`);
      }
      const path =
        hashParams.length > 0
          ? `${basePath}#${hashParams.join("&")}`
          : basePath;
      window.open(path);
    },
    seoTrackCurrentPage() {
      if (!this.personList || !this.city) {
        return;
      }
      let activeProfiles = 0;
      let inactiveProfiles = 0;
      let profilesWithReviews = 0;
      let profilesWith10Reviews = 0;
      let profilesWith100Reviews = 0;
      let profilesWithOrders = 0;
      let profilesWith10Orders = 0;
      let profilesWith100Orders = 0;
      for (const person of this.personList) {
        person.isActive ? activeProfiles++ : inactiveProfiles++;
        if (person.winCount >= 100) {
          profilesWith100Orders++;
        } else if (person.winCount >= 10) {
          profilesWith10Orders++;
        } else if (person.winCount > 0) {
          profilesWithOrders++;
        }
        if (person.scoreCount >= 100) {
          profilesWith100Reviews++;
        } else if (person.scoreCount >= 10) {
          profilesWith10Reviews++;
        } else if (person.scoreCount > 0) {
          profilesWithReviews++;
        }
      }

      this.$track.customEvent({
        event: "professionCityListPage",
        profession: this.profession.seoName,
        location: this.city.seoName,
        profiles: this.personList.length, // Hány szaki van összesen?
        activeProfiles, // Hány szaki aktív?
        inactiveProfiles, // Hány szaki inaktív?
        profilesWithReviews, // Hány szaki van legalább 1 értékeléssel?
        profilesWith10Reviews, // Hány szaki van legalább 10 értékeléssel?
        profilesWith100Reviews, // Hány szaki van legalább 100 értékeléssel?
        profilesWithOrders, // Hány szaki van legalább 1 megbízással?
        profilesWith10Orders, // Hány szaki van legalább 10 megbízással?
        profilesWith100Orders, // Hány szaki van legalább 100 megbízással?
      });
    },

    trackViewItemList() {
      this.$track.customEvent({
        event: "view_item_list",
        source_page: "nuxt_list",
        city_id: this.city ? this.city.id : null,
        profession_id: this.profession,
        item_list_name: this.profession.name, // TODO : What?
        item_list_id: this.profession.id, // TODO : What?
        ecommerce: {
          items: this.peopleIds.map((id) => {
            const people = this.personList.find((x) => x.id === id);
            // Use paginationInfo.current for page number (hash-based pagination)
            const currentPage = this.paginationInfo?.current ?? 1;
            const offset = (currentPage - 1) * this.limit;
            return {
              index: this.peopleIds.indexOf(id) + offset,
              item_id: people.id,
              item_name: people.name,
              item_list_name: `${this.profession.name} ${this.city.name}`,
              item_list_id: `${this.city.seoName}#${this.profession.seoName}`,
              item_category: this.profession.name,
              item_category2: this.city.name,
            };
          }),
        },
      });
    },
    trackOpenPersonProfile(person) {
      this.$track.customEvent({ ecommerce: null });
      // Use paginationInfo.current for page number (hash-based pagination)
      const currentPage = this.paginationInfo?.current ?? 1;
      const offset = (currentPage - 1) * this.limit;
      this.$track.customEvent({
        event: "select_item",
        city_id: this.city.id,
        profession_id: this.profession.id,
        ecommerce: {
          items: [
            {
              index: this.peopleIds.indexOf(person.id) + offset,
              item_id: person.id,
              item_name: person.name,
              item_list_name: `${this.profession?.name} ${this.city?.name}`,
              item_list_id: `${this.city?.seoName}#${this.profession?.seoName}`,
            },
          ],
        },
      });
    },
  },
};
</script>
